<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>
        Reporte de Ganancias
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              type="date"
              label="Fecha inicial"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.dateTo"
              type="date"
              label="Fecha final"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.groupBy"
              :items="groupOptions"
              item-text="text"
              item-value="value"
              label="Agrupar por"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.host"
              :items="cashRegisters"
              item-text="name"
              item-value="id"
              label="Caja"
              clearable
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-2">
          <v-btn color="primary" class="mr-2 mb-2" :loading="loading" @click="runReport">
            <v-icon left>mdi-play</v-icon>
            Ejecutar
          </v-btn>
          <v-btn text class="mr-2 mb-2" @click="clearFilters">
            <v-icon left>mdi-filter-remove-outline</v-icon>
            Limpiar filtros
          </v-btn>
          <v-btn color="success" class="mb-2" :loading="downloading" :disabled="rows.length === 0" @click="downloadReport">
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Excel
          </v-btn>
          <v-spacer></v-spacer>
          <div class="caption grey--text mb-2">
            {{ meta.totalCount }} periodos | Venta: {{ formatCurrency(meta.totals.saleTotal) }} | Costo: {{ formatCurrency(meta.totals.purchaseTotal) }} | Ganancia: {{ formatCurrency(meta.totals.profit) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>
        Ganancias por periodo
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        class="elevation-1"
        dense
        :footer-props="{ itemsPerPageText: 'Filas por pagina', itemsPerPageOptions: [10, 25, 50, 100] }"
      >
        <template #[`item.units`]="{ item }">
          {{ formatNumber(item.units) }}
        </template>
        <template #[`item.saleTotal`]="{ item }">
          {{ formatCurrency(item.saleTotal) }}
        </template>
        <template #[`item.purchaseTotal`]="{ item }">
          {{ formatCurrency(item.purchaseTotal) }}
        </template>
        <template #[`item.profit`]="{ item }">
          {{ formatCurrency(item.profit) }}
        </template>
        <template #[`item.marginPercent`]="{ item }">
          {{ formatPercent(item.marginPercent) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import * as salesService from '@/services/salesService';

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultFilters = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    dateFrom: formatInputDate(firstDay),
    dateTo: formatInputDate(today),
    groupBy: 'day',
    host: null,
  };
};

const defaultMeta = () => ({
  totalCount: 0,
  totals: {
    units: 0,
    saleTotal: 0,
    purchaseTotal: 0,
    profit: 0,
  },
});

export default {
  name: 'SalesProfitReportView',
  data() {
    return {
      loading: false,
      downloading: false,
      cashRegisters: [],
      rows: [],
      meta: defaultMeta(),
      filters: defaultFilters(),
      groupOptions: [
        { text: 'Dia', value: 'day' },
        { text: 'Semana', value: 'week' },
        { text: 'Mes', value: 'month' },
      ],
      headers: [
        { text: 'Periodo', value: 'periodLabel' },
        { text: 'Unidades', value: 'units', align: 'end' },
        { text: 'Venta', value: 'saleTotal', align: 'end' },
        { text: 'Costo', value: 'purchaseTotal', align: 'end' },
        { text: 'Ganancia', value: 'profit', align: 'end' },
        { text: 'Margen', value: 'marginPercent', align: 'end' },
      ],
    };
  },
  async mounted() {
    await this.loadCashRegisters();
    await this.runReport();
  },
  methods: {
    async loadCashRegisters() {
      try {
        this.cashRegisters = await salesService.fetchCashRegisters();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar cajas.', type: 'error' });
      }
    },
    cleanParams() {
      const params = { ...this.filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      return params;
    },
    async runReport() {
      if (!this.filters.dateFrom || !this.filters.dateTo) {
        this.$emit('show-alert', { message: 'Selecciona fecha inicial y final.', type: 'warning' });
        return;
      }

      this.loading = true;
      try {
        const response = await salesService.fetchSalesProfitReportData(this.cleanParams());
        this.rows = response.rows || [];
        this.meta = response.meta || defaultMeta();
      } catch (error) {
        this.rows = [];
        this.meta = defaultMeta();
        this.$emit('show-alert', { message: 'Error al ejecutar el reporte de ganancias.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = defaultFilters();
      this.runReport();
    },
    async downloadReport() {
      this.downloading = true;
      try {
        const blob = await salesService.downloadSalesProfitReport(this.cleanParams());
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_ganancias_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al descargar el reporte de ganancias.', type: 'error' });
      } finally {
        this.downloading = false;
      }
    },
    formatCurrency(value) {
      return `$${Number(value || 0).toFixed(2)}`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('es-MX');
    },
    formatPercent(value) {
      return `${Number(value || 0).toFixed(2)}%`;
    },
  },
};
</script>
